import { HttpClient } from '@angular/common/http'
import { inject, Injectable, InjectionToken, Signal, signal, WritableSignal } from '@angular/core'
import { lastValueFrom } from 'rxjs'
import { ENV_TOKEN } from '../../app.config'

/**
 * Woher `app-mitarbeiter-avatar` das Bild zu einer Profilbild-Id bekommt. Ein Token statt des
 * Services selbst: die Aufgabenzeile und ihre Nachbarn kennen bewusst keine Services und rendern
 * im Harness ohne HttpClient. Ohne Quelle zeigt der Avatar schlicht die Initialen.
 */
export interface ProfilbildQuelle {
  url(bildId: string): Signal<string | null>
}

export const PROFILBILD_QUELLE = new InjectionToken<ProfilbildQuelle>('PROFILBILD_QUELLE')

/**
 * Laedt Profilbilder der Kolleg:innen und haelt sie als Object-URL, EINMAL je Id und Seitenaufruf.
 *
 * Als Blob, weil der Endpunkt den Token braucht und ein `<img src>` ihn nicht mitschickt (dasselbe
 * Muster wie das Partner-Logo). Die Id wechselt mit jedem Upload, ein Bild zu einer Id aendert sich
 * also nie: deshalb kein Freigeben und kein Neuladen. Die Menge ist durch die Zahl der Kolleg:innen
 * begrenzt, jedes Bild hat rund 20 kB. Das Backend liefert es zusaetzlich mit einem Jahr
 * `Cache-Control`, ein Neuladen der Seite kommt also aus dem Browser-Cache.
 *
 * Ein Fehler (404 fuer ein gerade ersetztes Bild, Netz weg) laesst die URL auf `null`: der Avatar
 * zeigt dann Initialen. In der Fehlerzentrale erscheint der 404 nicht (Ignore-Liste in `app.config`).
 */
@Injectable({ providedIn: 'root' })
export class ProfilbildService implements ProfilbildQuelle {
  private readonly http = inject(HttpClient)
  private readonly apiUrl = inject(ENV_TOKEN).api.leistungserbringerUrl
  private readonly bilder = new Map<string, WritableSignal<string | null>>()

  url(bildId: string): Signal<string | null> {
    const vorhanden = this.bilder.get(bildId)
    if (vorhanden) return vorhanden
    const neu = signal<string | null>(null)
    this.bilder.set(bildId, neu)
    void this.laden(bildId, neu)
    return neu
  }

  /**
   * Nach dem eigenen Upload liegt das Bild schon im Browser. Es wird unter der neuen Id abgelegt,
   * damit Kopfleiste und Vorschau es ohne zweiten Abruf zeigen.
   */
  vorlegen(bildId: string, bild: Blob): void {
    const vorhanden = this.bilder.get(bildId)
    const url = URL.createObjectURL(bild)
    if (vorhanden) {
      vorhanden.set(url)
    } else {
      this.bilder.set(bildId, signal<string | null>(url))
    }
  }

  private async laden(bildId: string, ziel: WritableSignal<string | null>): Promise<void> {
    try {
      const bild = await lastValueFrom(
        this.http.get(`${this.apiUrl}/leistungserbringer/employees/profilbild/${encodeURIComponent(bildId)}`, { responseType: 'blob' }),
      )
      if (bild.size > 0) ziel.set(URL.createObjectURL(bild))
    } catch {
      // Initialen statt Bild; siehe Kommentar an der Klasse.
    }
  }
}
